import { useEffect, useRef, useState } from "react";
import { ref, onValue, update, off } from "firebase/database";
import { getRealtimeDatabase, getStorage } from "src/utils/firebase";

export default function CameraPage() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [activeUid, setActiveUid] = useState(null);
  const [status, setStatus] = useState("📡 Waiting for active session...");

  const db = getRealtimeDatabase();
  const storage = getStorage();

  // ✅ Helper to save selfie to Firebase Storage
  async function saveSelfie(uid, dataUrl) {
    const fileName = `selfies/${uid}_${Date.now()}.png`;
    const res = await fetch(dataUrl);
    const blob = await res.blob();

    const storageRef = storage.ref().child(fileName);
    await storageRef.put(blob);

    const url = await storageRef.getDownloadURL();
    return url;
  }

  // ✅ Start camera on mount
  useEffect(() => {
    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Error accessing camera:", err);
        setStatus("❌ Could not access your camera. Please check permissions.");
      }
    }
    startCamera();
  }, []);

  // Step 1: session watcher (always selfie)
  useEffect(() => {
    const sessionRef = ref(db, "demo_current_selfie");
    const handler = (snapshot) => {
      const uid = snapshot.val()?.uid;
      setActiveUid(uid || null);
    };

    onValue(sessionRef, handler);
    return () => off(sessionRef, "value", handler);
  }, [db]);

  // Step 2: snap watcher (always selfie users)
  useEffect(() => {
    if (!activeUid) return () => {};

    const userSnapRef = ref(db, `demo_users/selfie/${activeUid}/snap`);
    const handler = async (snapshot) => {
      if (snapshot.val() === true) {
        await captureAndUpload(activeUid);
      }
    };

    onValue(userSnapRef, handler);
    return () => off(userSnapRef, "value", handler);
  }, [activeUid, db]);

  // ✅ Capture + Upload selfie
  async function captureAndUpload(uid) {
    setStatus("📸 Snapping image...");
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const radius = 30;
    const border = 15;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // White rounded rectangle
    ctx.fillStyle = "white";
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.arcTo(canvas.width, 0, canvas.width, canvas.height, radius);
    ctx.arcTo(canvas.width, canvas.height, 0, canvas.height, radius);
    ctx.arcTo(0, canvas.height, 0, 0, radius);
    ctx.arcTo(0, 0, canvas.width, 0, radius);
    ctx.closePath();
    ctx.fill();

    // Clip inner rect
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(radius + border, border);
    ctx.arcTo(canvas.width - border, border, canvas.width - border, canvas.height - border, radius);
    ctx.arcTo(canvas.width - border, canvas.height - border, border, canvas.height - border, radius);
    ctx.arcTo(border, canvas.height - border, border, border, radius);
    ctx.arcTo(border, border, canvas.width - border, border, radius);
    ctx.closePath();
    ctx.clip();

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.restore();

    // Overlay logos
    const itarraLogo = new Image();
    itarraLogo.src = "/iTarra-LOGO-2022-Outline.webp";
    await new Promise((res) => (itarraLogo.onload = res));
    ctx.drawImage(itarraLogo, (canvas.width - 200) / 2, 80, 200, 60);

    const googleLogo = new Image();
    googleLogo.src = "/powered-by-google.png";
    await new Promise((res) => (googleLogo.onload = res));
    ctx.drawImage(googleLogo, (canvas.width - 180) / 2, canvas.height - 110, 180, 60);

    // Upload
    const dataUrl = canvas.toDataURL("image/png");
    setStatus("⬆️ Uploading...");
    const url = await saveSelfie(uid, dataUrl);

    await update(ref(db, `demo_users/selfie/${uid}`), {
      photoUrl: url,
      snap: false
    });

    setStatus("✅ Done snapping image");
    console.log("✅ Captured & uploaded selfie for", uid, url);
  }

  return (
    <div
      style={{
        backgroundColor: "black",
        color: "white",
        fontFamily: "sans-serif",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        margin: 0,
        padding: "50px",
        boxSizing: "border-box",
        position: "relative"
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "30px",
          border: "15px solid white",
          overflow: "hidden"
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover"
          }}
        >
          <track kind="captions" />
        </video>
      </div>

      <img
        src="/iTarra-LOGO-2022-Outline.webp"
        alt="iTarra Logo"
        style={{
          position: "absolute",
          top: "100px",
          left: "50%",
          transform: "translateX(-50%)",
          maxWidth: "200px",
          zIndex: 2
        }}
      />
      <img
        src="/powered-by-google.png"
        alt="Powered by Google Cloud"
        style={{
          position: "absolute",
          bottom: "120px",
          left: "50%",
          transform: "translateX(-50%)",
          maxHeight: "80px",
          zIndex: 2
        }}
      />

      <div
        style={{
          position: "absolute",
          bottom: "20px",
          left: "0",
          right: "0",
          textAlign: "center",
          fontSize: "18px",
          background: "rgba(0,0,0,0.5)",
          padding: "8px",
          borderRadius: "8px",
          zIndex: 3
        }}
      >
        {activeUid ? (
          <>
            🎯 Active Session:{" "}
            <span style={{ fontFamily: "monospace" }}>{activeUid}</span>
            <br />
            {status}
          </>
        ) : (
          status
        )}
      </div>

      <canvas ref={canvasRef} width="640" height="480" style={{ display: "none" }} />
    </div>
  );
}
